import { ApiFailure, api, onAuthLostHandler, setToken } from '@/api/client'
import { storageKey } from '@/app.config'
import {
  type ApiErrorCode,
  type AttendanceStatus,
  type DayKey,
  type ImportMembersResponse,
  type LocalData,
  type Member,
  NOTHING_PENDING,
  type PushRejectCode,
  type SyncOp,
  type UpdateMemberRequest,
  type UpdateUserRequest,
  type UserPublic,
  applyPull,
  emptyData,
  normalizeReason,
} from '@cadoan/shared'
import { create } from 'zustand'

/**
 * The console's state. Online-only (ADR 001): every change goes straight to
 * the API and the copy in memory is refreshed from the same change-number pull
 * the phones use, so the console and the phones read one truth.
 */

const SESSION_KEY = storageKey('session')

interface Session {
  token: string
  user: UserPublic
}

export class PushRejected extends Error {
  constructor(readonly code: PushRejectCode) {
    super(code)
  }
}

interface State {
  status: 'loading' | 'signedOut' | 'signedIn'
  user: UserPublic | null
  signedOutReason: 'expired' | 'locked' | null
  data: LocalData
  cursor: number
  loaded: boolean
  refreshing: boolean
  loadError: 'network' | 'server' | null
  users: UserPublic[]

  boot: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  changePassword: (current: string, next: string) => Promise<void>
  refresh: () => Promise<void>

  saveMeeting: (m: {
    id?: string
    date: DayKey
    time: string | null
    note: string
  }) => Promise<string>
  deleteMeeting: (id: string) => Promise<void>
  setMark: (
    meetingId: string,
    memberId: string,
    status: AttendanceStatus,
    reason: string | null,
  ) => Promise<void>

  createMember: (name: string, joinedOn: DayKey) => Promise<Member>
  updateMember: (id: string, body: UpdateMemberRequest) => Promise<Member>
  reorderMembers: (ids: string[]) => Promise<void>
  importMembers: (names: string[], joinedOn: DayKey) => Promise<ImportMembersResponse>

  loadUsers: () => Promise<void>
  createUser: (body: Parameters<typeof api.createUser>[0]) => Promise<UserPublic>
  updateUser: (id: string, body: UpdateUserRequest) => Promise<void>
  resetPassword: (id: string, password: string) => Promise<void>

  createReason: (label: string) => Promise<void>
  renameReason: (id: string, label: string) => Promise<void>
  deleteReason: (id: string) => Promise<void>
  reorderReasons: (ids: string[]) => Promise<void>
}

function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as Session) : null
  } catch {
    return null
  }
}

function writeSession(s: Session | null): void {
  try {
    if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s))
    else localStorage.removeItem(SESSION_KEY)
  } catch {
    /* storage blocked: the session lasts for this tab only */
  }
}

let lastStamp = 0
const stamp = () => {
  lastStamp = Math.max(Date.now(), lastStamp + 1)
  return lastStamp
}

const newId = (prefix: string) => `${prefix}_${crypto.randomUUID()}`

export const useConsoleStore = create<State>()((set, get) => {
  function signedOut(reason: State['signedOutReason']) {
    setToken(null)
    writeSession(null)
    set({
      status: 'signedOut',
      user: null,
      signedOutReason: reason,
      data: emptyData(),
      cursor: 0,
      loaded: false,
      users: [],
    })
  }

  onAuthLostHandler((code: ApiErrorCode) => {
    if (get().status !== 'signedIn') return
    signedOut(code === 'account_locked' ? 'locked' : 'expired')
  })

  async function pushOne(op: SyncOp): Promise<void> {
    const res = await api.push([op])
    const r = res.results[0]
    if (r && !r.ok) throw new PushRejected(r.code)
    await get().refresh()
  }

  function startSession(s: Session) {
    setToken(s.token)
    writeSession(s)
    set({ status: 'signedIn', user: s.user, signedOutReason: null })
  }

  return {
    status: 'loading',
    user: null,
    signedOutReason: null,
    data: emptyData(),
    cursor: 0,
    loaded: false,
    refreshing: false,
    loadError: null,
    users: [],

    async boot() {
      const saved = readSession()
      if (!saved) return set({ status: 'signedOut' })
      setToken(saved.token)
      try {
        const user = await api.me()
        startSession({ token: saved.token, user })
        await get().refresh()
      } catch (err) {
        if (err instanceof ApiFailure && err.kind === 'api') return signedOut('expired')
        // Offline or the API is down: keep the session, show the error state.
        startSession(saved)
        set({
          loadError: err instanceof ApiFailure && err.kind === 'network' ? 'network' : 'server',
        })
      }
    },

    async login(username, password) {
      const res = await api.login(username, password)
      set({ data: emptyData(), cursor: 0, loaded: false })
      startSession({ token: res.token, user: res.user })
      if (!res.user.mustChangePassword) await get().refresh()
    },

    async logout() {
      try {
        await api.logout()
      } catch {
        /* signing out locally is what matters */
      }
      signedOut(null)
    },

    async changePassword(current, next) {
      await api.changePassword({ currentPassword: current, newPassword: next })
      const user = get().user
      if (user) {
        const updated = { ...user, mustChangePassword: false }
        const token = readSession()?.token
        if (token) writeSession({ token, user: updated })
        set({ user: updated })
      }
      if (!get().loaded) await get().refresh()
    },

    async refresh() {
      if (get().status !== 'signedIn' || get().user?.mustChangePassword) return
      set({ refreshing: true })
      try {
        const pull = await api.pull(get().cursor)
        const { data } = applyPull(get().data, pull, NOTHING_PENDING)
        set({ data, cursor: pull.rev, loaded: true, loadError: null })
      } catch (err) {
        if (!(err instanceof ApiFailure && err.kind === 'api')) {
          set({
            loadError: err instanceof ApiFailure && err.kind === 'network' ? 'network' : 'server',
          })
        }
        throw err
      } finally {
        set({ refreshing: false })
      }
    },

    async saveMeeting(m) {
      const id = m.id ?? newId('mt')
      await pushOne({
        kind: 'meeting',
        id,
        date: m.date,
        time: m.time || null,
        note: m.note,
        at: stamp(),
      })
      return id
    },

    async deleteMeeting(id) {
      await api.deleteMeeting(id)
      await get().refresh()
    },

    async setMark(meetingId, memberId, status, reason) {
      await pushOne({
        kind: 'attendance',
        meetingId,
        memberId,
        status,
        reason: normalizeReason(status, reason),
        at: stamp(),
      })
    },

    async createMember(name, joinedOn) {
      const m = await api.createMember(name, joinedOn)
      await get().refresh()
      return m
    },

    async updateMember(id, body) {
      const m = await api.updateMember(id, body)
      await get().refresh()
      return m
    },

    async reorderMembers(ids) {
      // Show the new order at once; the refresh confirms it.
      const members = new Map(get().data.members)
      ids.forEach((id, i) => {
        const m = members.get(id)
        if (m) members.set(id, { ...m, sortOrder: i + 1 })
      })
      set({ data: { ...get().data, members } })
      await api.reorderMembers(ids)
      await get().refresh()
    },

    async importMembers(names, joinedOn) {
      const res = await api.importMembers(names, joinedOn)
      await get().refresh()
      return res
    },

    async loadUsers() {
      set({ users: await api.listUsers() })
    },

    async createUser(body) {
      const u = await api.createUser(body)
      await get().loadUsers()
      return u
    },

    async updateUser(id, body) {
      await api.updateUser(id, body)
      await get().loadUsers()
      if (body.displayName !== undefined) await get().refresh()
    },

    async resetPassword(id, password) {
      await api.resetPassword(id, password)
      await get().loadUsers()
    },

    async createReason(label) {
      await api.createReason(label)
      await get().refresh()
    },
    async renameReason(id, label) {
      await api.renameReason(id, label)
      await get().refresh()
    },
    async deleteReason(id) {
      await api.deleteReason(id)
      await get().refresh()
    },
    async reorderReasons(ids) {
      await api.reorderReasons(ids)
      await get().refresh()
    },
  }
})
